import { FC, useContext, useState, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import axios from 'axios';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import Head from 'components/profileNavigation/Head';
import MenteeFormDisplay from 'components/UserForms/MenteeForm/MenteeFormDisplay';
import MenteeFormEdit from 'components/UserForms/MenteeForm/MenteeFormEdit';
import Button from 'components/Button';
import { apiURL } from 'utils/apiURL'; 
import MenteeFormType from 'types/MenteeForm'; 
import { initialErrors, State } from 'components/UserForms/MenteeForm/types';
import { MenteeEditContext } from './index';

const MenteeForm: FC = () => {
  const { mentee } = useContext(MenteeEditContext);
  
  const relativeEndpoint = `/mentee-form/${mentee?.id}`;
  const fullEndpoint = `${apiURL}${relativeEndpoint}`;
  
  const { data: menteeForm, error } = useSWR<MenteeFormType>(
    mentee?.id ? relativeEndpoint : null,
  { 
    revalidateOnFocus: false, // Ne pas rafraîchir quand vous changez de fenêtre
    revalidateIfStale: false,  // Ne pas rafraîchir si la donnée est déjà là
    dedupingInterval: 60000    // Ignorer les requêtes identiques pendant 1 minute
  }
  );

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [state, setState] = useState<State>({} as State);
  const [errors, setErrors] = useState(initialErrors);

useEffect(() => {
  if (menteeForm && !isInitialized) {
    const rawData = menteeForm as any;
    
    const sanitizedState: State = {
      ...rawData,

      expertises: rawData.expertises || [],
      hobbies: rawData.hobbies || [],
      priorities: rawData.priorities || [],
      considerations: rawData.considerations || [],
      volunteerState: rawData.volunteerState ?? rawData.volunteer ?? "",
      expectations: rawData.expectations || "",
      heardAboutMentorshipText: rawData.heardAboutMentorshipText || "",
    } as State;

    setState(sanitizedState);
    setIsInitialized(true);
  }
}, [menteeForm, isInitialized]);

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);

  const token = localStorage.getItem('accessToken'); 

  try {

    await axios.put(fullEndpoint, state, {
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      }
    });
    
    await mutate(relativeEndpoint);
    setIsEditing(false);
    alert("Modifications enregistrées !");
  } catch (err) {
    console.error("Erreur save:", err);
    alert("Erreur lors de l'enregistrement.");
  } finally {
    setLoading(false);
  }
};

  if (error) return <p style={{ padding: '2rem' }}>L'utilisateur n'a pas encore rempli son formulaire.</p>;
  if (!menteeForm || !isInitialized) return <LoaderContainer height="20vh"><DefaultLoader /></LoaderContainer>;

  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <Head title="Formulaire mentoré" subtitle="Informations saisies lors de l'inscription" />
        <Button color={isEditing ? 'default' : 'primary'} onClick={() => setIsEditing(!isEditing)}>
          {isEditing ? 'Annuler' : 'Modifier les informations'}
        </Button>
      </div>

      <div>
        {isEditing ? (
          <MenteeFormEdit
            title="" subtitle=""
            state={state} setState={setState}
            loading={loading} handleSubmit={handleSubmit}
            errors={errors} setErrors={setErrors}
          />
        ) : (
          <MenteeFormDisplay menteeForm={state} />
        )}
      </div>
    </section>
  );
};

export default MenteeForm;